import { DRYDOCK_AUDIT_GROUPS, type DrydockAuditGroup, drydockAuditGroup } from '@felix/domain';
import { Alert, Drawer, Select } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { zonedStamp } from '../../inputs/lt-utc.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { TableSkeleton } from '../purchasing/table.js';
import classes from './Drydock.module.css';
import { useDrydockErrorText } from './errors.js';
import type { DrydockProjectSource } from './source.js';
import type { DrydockAudit, DrydockAuditEntry, DrydockProject } from './types.js';

type Filter = 'all' | DrydockAuditGroup;

const TONE: Partial<Record<DrydockAuditEntry['kind'], 'success' | 'warning' | 'danger'>> = {
  job_approved: 'success',
  job_verified: 'success',
  finding_closure_verified: 'success',
  project_closed: 'success',
  job_returned: 'warning',
  job_verification_returned: 'warning',
  finding_closure_returned: 'warning',
  finding_reopened: 'warning',
  result_corrected: 'warning',
  job_rejected: 'danger',
  project_cancelled: 'danger',
  inspection_cancelled: 'danger',
  finding_cancelled: 'danger',
  job_cancelled: 'danger',
  evidence_voided: 'danger',
  link_voided: 'danger',
};

/** Girişler süzgece göre (gruplar: proje, denetim, bulgular, işler, tersane, ofis). */
export function filterAudit(
  entries: readonly DrydockAuditEntry[],
  filter: Filter,
): DrydockAuditEntry[] {
  return filter === 'all'
    ? [...entries]
    : entries.filter((e) => drydockAuditGroup(e.kind) === filter);
}

/**
 * Projenin denetim izi (ADR 0129 Ek Faz 5; Excel Screen 12, R7 "Auditability", R17): oluşturma,
 * düzenleme (değişen alanlar), sonuç ve düzeltmesi, inceleme (kendi incelemesi işaretli), kanıt,
 * bulgu, iş, bağ, ofise gönderim, ofis kararı (parola işaretiyle), tersane olayları, doğrulama ve
 * kapanış. Zaman gemi saati (LT) ve UTC; ofis kararı yalnız UTC. Yalnız okur [NR496 4.6.3].
 */
export function DrydockAuditDrawer({
  project,
  source,
  onClose,
}: {
  project: DrydockProject | null;
  source: DrydockProjectSource;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const errorText = useDrydockErrorText();
  const a = (key: string, values: Record<string, unknown> = {}) => t(`drydockAudit.${key}`, values);
  const [audit, setAudit] = useState<DrydockAudit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const projectId = project?.id ?? null;

  useEffect(() => {
    let alive = true;
    setAudit(null);
    setError(null);
    if (!projectId || !source.audit) return;
    source
      .audit(projectId)
      .then((value) => {
        if (alive) setAudit(value);
      })
      .catch((err) => {
        if (alive) setError(errorText(err));
      });
    return () => {
      alive = false;
    };
  }, [projectId, source, errorText]);

  const rows = audit ? filterAudit(audit.entries, filter) : [];
  return (
    <Drawer
      opened={project !== null}
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('drydock.close') }}
      position="right"
      size="xl"
      title={project ? a('title', { no: project.projectNo }) : ''}
    >
      <div className={classes.detail}>
        <p className={classes.muted}>{a('hint')}</p>
        <div className={classes.filters}>
          <Select
            label={a('filter')}
            data={[
              { value: 'all', label: a('all') },
              ...DRYDOCK_AUDIT_GROUPS.map((g) => ({ value: g, label: a(`groups.${g}`) })),
            ]}
            value={filter}
            allowDeselect={false}
            onChange={(value) => setFilter((value as Filter | null) ?? 'all')}
          />
        </div>
        {error ? (
          <Alert color="red" title={a('error')}>
            {error}
          </Alert>
        ) : audit === null ? (
          <TableSkeleton columns={5} label={a('open')} />
        ) : rows.length === 0 ? (
          <EmptyState title={a('empty')} />
        ) : (
          <AuditTable entries={rows} label={a('open')} />
        )}
      </div>
    </Drawer>
  );
}

/** İz tablosu: zaman (LT ve UTC; ofis kararı yalnız UTC), kayıt, olay ve işaretleri, kişi, not. */
export function AuditTable({
  entries,
  label,
}: {
  entries: readonly DrydockAuditEntry[];
  label: string;
}) {
  const { t } = useTranslation();
  const a = (key: string, values: Record<string, unknown> = {}) => t(`drydockAudit.${key}`, values);
  const rows = entries;
  return (
    <section
      className={table.tableWrap}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
      tabIndex={0}
    >
      <table className={`${table.table} ${classes.checkpointTable}`}>
        <thead>
          <tr>
            <th scope="col">{a('columns.at')}</th>
            <th scope="col">{a('columns.record')}</th>
            <th scope="col">{a('columns.event')}</th>
            <th scope="col">{a('columns.by')}</th>
            <th scope="col">{a('columns.note')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => {
            const marks = [
              e.selfReview ? a('marks.selfReview') : null,
              e.reauthenticated ? a('marks.reauthenticated') : null,
              e.provisional ? a('marks.provisional') : null,
            ].filter(Boolean);
            const detail = [
              e.fields.length > 0 ? a('fields', { list: e.fields.join(', ') }) : null,
              e.percent !== null ? a('percent', { percent: e.percent }) : null,
              e.note,
            ].filter(Boolean);
            return (
              <tr key={`${e.kind}-${e.subject.id}-${e.at}-${e.by}`}>
                <td className={classes.mono}>
                  {e.tzOffsetMin !== null ? (
                    <>
                      {`${zonedStamp(e.at, e.tzOffsetMin)} LT`}
                      <br />
                    </>
                  ) : null}
                  <span className={classes.muted}>{`${zonedStamp(e.at)} UTC`}</span>
                </td>
                <td className={classes.mono}>{e.subject.label ?? '—'}</td>
                <td>
                  <StatusBadge tone={TONE[e.kind] ?? 'neutral'}>{a(`kinds.${e.kind}`)}</StatusBadge>
                  {marks.length > 0 ? (
                    <div className={classes.muted}>{marks.join(' · ')}</div>
                  ) : null}
                </td>
                <td>
                  {e.byName ?? '—'}
                  <div className={classes.muted}>{a(`sides.${e.side}`)}</div>
                </td>
                <td>{detail.length > 0 ? detail.join(' · ') : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
